/**
 * Breakdown View
 * Displays betting breakdown by different categories at parlay or leg level
 */

import { TableSection } from '@components/ui';
import { AnalyticsFilterBar } from '@components/ui/AnalyticsFilterBar';
import type { AnalysisLevel, AnalyticsFilter } from '@parlay-picker/shared/analyticsScopes';
import type { FactorPerformance } from '@parlay-picker/shared/breakdownTypes';
import type { GroupStats, LegStats, PlayerStats } from '@utils/summaryCalculations';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface DateRange {
  from: Date;
  to: Date;
}

type BreakdownData = Record<string, GroupStats>;

interface BreakdownViewProps {
  byLeague: BreakdownData;
  byLegs: BreakdownData;
  byOdds: BreakdownData;
  byMode?: BreakdownData;
  legStats: LegStats;
  factorPerformance?: FactorPerformance[];
  dateRange?: DateRange | null;
  filter: AnalyticsFilter;
  onFilterChange: (filter: AnalyticsFilter) => void;
  level: AnalysisLevel;
  onLevelChange: (level: AnalysisLevel) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

// Format date range for display
const formatDateRange = (dateRange: DateRange | null | undefined): string | null => {
  if (!dateRange?.from || !dateRange?.to) return null;
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const from = dateRange.from.toLocaleDateString('en-US', options);
  const to = dateRange.to.toLocaleDateString('en-US', options);
  if (from === to) return from;
  return `${from} - ${to}`;
};

// =============================================================================
// Component
// =============================================================================

export function BreakdownView({
  byLeague,
  byLegs,
  byOdds,
  byMode,
  legStats,
  factorPerformance,
  dateRange,
  filter,
  onFilterChange,
  level,
  onLevelChange,
}: BreakdownViewProps): ReactElement {
  const formattedDateRange = formatDateRange(dateRange);

  return (
    <div className="space-y-6">
      <AnalyticsFilterBar
        showFilter
        filter={filter}
        onFilterChange={onFilterChange}
        showLevel
        level={level}
        onLevelChange={onLevelChange}
      />
      {level === 'legs' ? (
        <LegsBreakdown legStats={legStats} factorPerformance={factorPerformance} />
      ) : (
        <>
          {formattedDateRange && (
            <div className="text-center text-sm text-theme-text-secondary">
              Based on bets from{' '}
              <span className="text-theme-text-secondary">{formattedDateRange}</span>
            </div>
          )}
          <div className="grid @3xl:grid-cols-2 gap-6">
            <TableSection title="By League" data={byLeague} />
            <TableSection title="By # of Legs" data={byLegs} keyOrder={['2', '3', '4', '5+']} />
            <TableSection
              title="By Odds Range"
              data={byOdds}
              keyOrder={['+100 to +250', '+251 to +500', '+501 to +1000', '+1000+']}
            />
            {byMode && Object.keys(byMode).length > 0 && (
              <TableSection
                title="By Analysis Mode"
                data={byMode}
                keyOrder={['standard', 'detailed', 'premium']}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Leg-level Breakdown
// =============================================================================

function LegBreakdownTable({
  title,
  data,
}: {
  title: string;
  data: Record<string, PlayerStats>;
}): ReactElement {
  const entries = Object.entries(data).sort((a, b) => {
    const rateA = a[1].total > 0 ? a[1].hits / a[1].total : 0;
    const rateB = b[1].total > 0 ? b[1].hits / b[1].total : 0;
    return rateB - rateA;
  });

  return (
    <div className="bg-theme-surface rounded-lg p-4">
      <h3 className="text-white font-bold mb-3">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-theme-text-tertiary text-sm">No data yet</p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-1">
          {entries.map(([name, stats]) => {
            const rate = stats.total > 0 ? (stats.hits / stats.total) * 100 : 0;
            return (
              <div
                key={name}
                className="flex justify-between items-center text-sm py-1 border-b border-theme-border/30 last:border-0"
              >
                <span className="text-theme-text-secondary truncate mr-2">{name}</span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-theme-text-tertiary">
                    {stats.hits}/{stats.total}
                  </span>
                  <span
                    className={`font-medium min-w-[3rem] text-right ${
                      rate >= 50 ? 'text-theme-success' : 'text-theme-danger'
                    }`}
                  >
                    {rate.toFixed(0)}%
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LegsBreakdown({
  legStats,
  factorPerformance,
}: {
  legStats: LegStats;
  factorPerformance?: FactorPerformance[];
}): ReactElement {
  return (
    <div className="grid @3xl:grid-cols-2 gap-6">
      <LegBreakdownTable title="By Player/Team" data={legStats.byPlayer} />
      <LegBreakdownTable title="By Prop Type" data={legStats.byPropType} />
      {factorPerformance && factorPerformance.length > 0 && (
        <div className="@3xl:col-span-2">
          <FactorBreakdownTable data={factorPerformance} />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Factor Performance Table (ON vs OFF hit rates)
// =============================================================================

function FactorBreakdownTable({ data }: { data: FactorPerformance[] }): ReactElement {
  // Filter to only factors with meaningful data on both sides, then sort by lift descending
  const sortedFactors = [...data]
    .filter((f) => f.enabledTotal >= 3 || f.disabledTotal >= 3)
    .sort((a, b) => b.lift - a.lift);

  if (sortedFactors.length === 0) {
    return (
      <div className="bg-theme-surface rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3 text-theme-text">By Factor</h3>
        <p className="text-theme-text-tertiary text-sm">Not enough data yet</p>
      </div>
    );
  }

  return (
    <div className="bg-theme-surface rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3 text-theme-text">By Factor</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-theme-surface">
            <tr className="text-theme-text-secondary text-left">
              <th className="pb-2">Factor</th>
              <th className="pb-2 text-right">ON Rate</th>
              <th className="pb-2 text-right">OFF Rate</th>
              <th className="pb-2 text-right">Lift</th>
              <th className="pb-2 text-right">Sample</th>
            </tr>
          </thead>
          <tbody>
            {sortedFactors.map((f) => {
              const liftColor =
                f.lift > 0
                  ? 'text-theme-success'
                  : f.lift < 0
                    ? 'text-theme-danger'
                    : 'text-theme-text-secondary';

              return (
                <tr key={f.factor} className="border-t border-theme-border">
                  <td className="py-2 text-theme-text">{f.factor}</td>
                  <td className="py-2 text-right text-theme-text-secondary">
                    {f.enabledTotal > 0 ? `${(f.enabledRate * 100).toFixed(1)}%` : '-'}
                  </td>
                  <td className="py-2 text-right text-theme-text-secondary">
                    {f.disabledTotal > 0 ? `${(f.disabledRate * 100).toFixed(1)}%` : '-'}
                  </td>
                  <td className={`py-2 text-right font-medium ${liftColor}`}>
                    {f.lift > 0 ? '+' : ''}
                    {(f.lift * 100).toFixed(1)}
                  </td>
                  <td className="py-2 text-right text-theme-text-tertiary">
                    {f.enabledTotal + f.disabledTotal}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
